import React, { createContext, useContext, useState, type ReactNode } from "react";
import type { TabStructure } from "@/features/inventory/item-detail/utils/buildTabsFromMeta";
import { captureTabClicked } from "@/lib/analytics/posthog-events";

interface DetailPageTabsContextValue {
  tabs: TabStructure[];
  activeTab: string;
  tabHistory: string[]; // Most recent first
  setTabs: React.Dispatch<React.SetStateAction<TabStructure[]>>;
  setActiveTab: (tab: string, shouldMoveToFirst?: boolean) => void;
  /**
   * Reset tabs state when switching between different detail pages.
   *
   * This prevents "tab bleed" where a previous doctype's tab structure can
   * momentarily (or permanently, if tab IDs overlap) render on the next page.
   */
  resetForScope: (scopeKey: string) => void;
}

const DetailPageTabsContext = createContext<DetailPageTabsContextValue | null>(null);

export function DetailPageTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<TabStructure[]>([]);
  const [activeTab, setActiveTab] = useState<string>("");
  const [tabHistory, setTabHistory] = useState<string[]>([]);
  const scopeKeyRef = React.useRef<string>("");

  const handleSetActiveTab = (tab: string, shouldMoveToFirst: boolean = true) => {
    setActiveTab(tab);
    captureTabClicked(tab);
    
    // Only update history if we should move the tab to first position
    // If tab is already in positions 2-3, don't move it
    if (shouldMoveToFirst) {
      // Update history: move this tab to the front, remove duplicates
      setTabHistory(prev => {
        const filtered = prev.filter(t => t !== tab);
        return [tab, ...filtered];
      });
    }
  };

  const resetForScope = React.useCallback((scopeKey: string) => {
    const next = String(scopeKey || "");
    if (!next) return;
    if (scopeKeyRef.current === next) return;
    scopeKeyRef.current = next;
    setTabs([]);
    setActiveTab("");
    setTabHistory([]);
  }, []);

  return (
    <DetailPageTabsContext.Provider
      value={{
        tabs,
        activeTab,
        tabHistory,
        setTabs,
        setActiveTab: handleSetActiveTab,
        resetForScope,
      }}
    >
      {children}
    </DetailPageTabsContext.Provider>
  );
}

export function useDetailPageTabs() {
  const context = useContext(DetailPageTabsContext);
  if (!context) {
    throw new Error("useDetailPageTabs must be used within DetailPageTabsProvider");
  }
  return context;
}


/** Same as useDetailPageTabs but returns null outside a provider (registry / isolated tests). */
export function useOptionalDetailPageTabs() {
  return useContext(DetailPageTabsContext);
}
